import React, {useEffect, useState} from "react";
import "./InfoOverlay.css";
import {
  ChargingStationPOI,
  ConstructionSitePOI,
  PointOfInterest,
  WifiHotspotPOI,
} from "../logic/APITypes";
import {Column} from "primereact/column";
import {DataTable} from "primereact/datatable";
import {usePlaceIdSearch} from "../logic/usePlaceIdSearch";

function InfoOverlay(props: {
  poi: PointOfInterest;
  onClose: () => void;
  kiosk?: boolean;
}) {
  const [animating, setAnimating] = useState(true);

  useEffect(() => setAnimating(false), []);

  return (
    <div
      className={"infoOverlay" + (animating ? " infoOverlay--animating" : "")}
    >
      <div className={"infoOverlay__header"}>
        <div className={"infoOverlay__title"}>{props.poi.name}</div>
        <button
          className={"infoOverlay__closeButton iconButton"}
          onClick={() => {
            setAnimating(true);
            setTimeout(() => props.onClose(), 500);
          }}
        >
          <i className="pi pi-times" title="Details ausblenden"></i>
        </button>
      </div>
      <div className={"infoOverlay__body"}>
        {props.poi.type === "construction" ? (
          <InfoOverlayConstruction poi={props.poi} />
        ) : props.poi.type === "charging" ? (
          <InfoOverlayCharging poi={props.poi} kiosk={props.kiosk} />
        ) : (
          <InfoOverlayWifi poi={props.poi} />
        )}
      </div>
    </div>
  );
}

function InfoOverlayConstruction(props: {poi: ConstructionSitePOI}) {
  return (
    <>
      {(props.poi.startDate !== "–" || props.poi.endDate !== "–") && (
        <div className="infoOverlay__row">
          <span>Start: {props.poi.startDate}</span>
          <span>Ende: {props.poi.endDate}</span>
        </div>
      )}
      {props.poi.contactInfo && (
        <div className="infoOverlay__row infoOverlay__row--tight">
          <span>Kontakt:</span>
          {props.poi.contactInfo.mail && (
            <a
              href={"mailto:" + props.poi.contactInfo.mail}
              rel="noreferrer"
              className="iconLink"
            >
              <i className="pi pi-envelope" />
            </a>
          )}
          {props.poi.contactInfo.website && (
            <a
              href={props.poi.contactInfo.website}
              rel="noreferrer"
              target="_blank"
              className="iconLink"
            >
              <i className="pi pi-globe" />
            </a>
          )}
        </div>
      )}
      {props.poi.details && (
        <div dangerouslySetInnerHTML={{__html: props.poi.details}} />
      )}
      {props.poi.media?.img &&
        props.poi.media.img.map((img, i) => (
          <img
            width={"100%"}
            key={props.poi.id + "image" + i}
            src={img.url}
            alt={img.cpt}
          />
        ))}
    </>
  );
}

function InfoOverlayCharging(props: {
  poi: ChargingStationPOI;
  kiosk?: boolean;
}) {
  const {id} = usePlaceIdSearch("WVV Energie Ladestation " + props.poi.address);

  return (
    <>
      <div className="infoOverlay__address">
        Adresse: {props.poi.address}
        {props.poi.description && (
          <>
            <br />
            <div className="infoOverlay__description">
              {props.poi.description.split("\n").map((line, i) => (
                <React.Fragment key={`description${props.poi.id}line${i}`}>
                  {i > 0 && <br />}
                  {line}
                </React.Fragment>
              ))}
            </div>
          </>
        )}
      </div>

      <div className="infoOverlay__row infoOverlay__row--tight">
        Route:
        <a
          className={"iconLink" + (props.kiosk ? " iconLink--disabled" : "")}
          rel="noreferrer"
          target="_blank"
          href={`http://maps.apple.com/?q=${encodeURI(props.poi.name)}&ll=${props.poi.lat},${props.poi.lng}`}
          aria-disabled={props.kiosk ? true : undefined}
          tabIndex={props.kiosk ? -1 : undefined}
        >
          <i className="pi pi-apple" />
        </a>
        <a
          className={"iconLink" + (props.kiosk ? " iconLink--disabled" : "")}
          rel="noreferrer"
          target="_blank"
          href={
            id
              ? `https://www.google.com/maps/search/?api=1&query=${encodeURI("WVV Energie Ladestation")}&query_place_id=${id}`
              : `https://www.google.com/maps/search/?api=1&query=${encodeURI("WVV Energie Ladestation " + props.poi.address)}`
          }
          aria-disabled={props.kiosk ? true : undefined}
          tabIndex={props.kiosk ? -1 : undefined}
        >
          <i className="pi pi-google" />
        </a>
      </div>
      <div className="infoOverlay__row">Öffnungszeiten: {props.poi.hours}</div>
      <div className="infoOverlay__row infoOverlay__row--tight">
        Preise:
        <a
          className={"iconLink" + (props.kiosk ? " iconLink--disabled" : "")}
          rel="noreferrer"
          target="_blank"
          href={props.poi.priceLink}
          aria-disabled={props.kiosk ? true : undefined}
          tabIndex={props.kiosk ? -1 : undefined}
        >
          <i className="pi pi-external-link" />
        </a>
      </div>
      <div className="infoOverlay__table">
        <DataTable value={props.poi.circuits}>
          <Column field="plug" header="Stecker" />
          <Column field="powerType" header="Art" />
          <Column field="maxPower" header="max. Leistung" />
          <Column
            field="status"
            header="Status"
            body={(poi) => (
              <span
                className={
                  "infoOverlay__statusCell infoOverlay__statusCell--" +
                  (poi.status === "Status lädt…"
                    ? "loading"
                    : poi.status === "Frei"
                      ? "free"
                      : poi.status === "Belegt"
                        ? "occupied"
                        : "unavailable")
                }
              >
                {poi.status}
              </span>
            )}
          />
        </DataTable>
      </div>
      {props.poi.imageLink && (
        <img
          width={"100%"}
          key={props.poi.id + "image0"}
          src={props.poi.imageLink}
          alt=""
        />
      )}
    </>
  );
}

function InfoOverlayWifi(props: {poi: WifiHotspotPOI}) {
  return <>{}</>;
}

export default InfoOverlay;
